import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, computed } from '@angular/core';
import { PendingChanges } from '@guards/pending-changes.guard';
import {
  ListDetailSplitViewComponent,
} from '@components/split-view/list-detail-split-view/list-detail-split-view.component';
import {
  CustomFieldCollectionDetailDto,
  CustomFieldCollectionItemDto,
  CustomFieldCollectionMergeDto,
  CustomFieldCollectionService,
  CustomFieldMergeDto,
} from '@artemis-software/wr-api';
import { BehaviorSubject, combineLatest, debounceTime, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import {
  CustomFieldCollectionDialogComponent,
} from '@pages/custom-fields-settings-page/custom-field-collection-split-view/custom-field-collection-dialog/custom-field-collection-dialog.component';
import { catalogTenantId } from '@/utils/admin-utils';
import { CurrentUserService } from '@services/current-user.service';

@Component({
  standalone: false,
  selector: 'wr-custom-field-collection-split-view',
  templateUrl: './custom-field-collection-split-view.component.html',
  styleUrls: ['./custom-field-collection-split-view.component.scss'],
})
export class CustomFieldCollectionSplitViewComponent implements PendingChanges, OnInit, OnDestroy {

  @ViewChild(ListDetailSplitViewComponent)
  splitView?: ListDetailSplitViewComponent<CustomFieldCollectionItemDto, CustomFieldCollectionDetailDto>;

  @ViewChild('folderDialog') folderDialog?: TemplateRef<unknown>;

  readonly loadDetail = (item: CustomFieldCollectionItemDto) => this.customFieldCollectionService.findById(item.id);
  readonly saveAction = (val: CustomFieldCollectionMergeDto) => this.save(val);
  readonly deleteAction = (val: CustomFieldCollectionMergeDto) => this.delete(val.id!);
  readonly deleteDialogAction = () => this.deleteDialog();

  readonly currentTenantId = catalogTenantId();
  readonly canCreate = computed(() => this.currentUserService.hasPrivilege('canCreateGenericForms'));

  loading$ = new BehaviorSubject<boolean>(false);
  searchText$ = new BehaviorSubject<string>('');
  reload$ = new BehaviorSubject<void>(undefined);
  collections$ = new BehaviorSubject<CustomFieldCollectionItemDto[] | undefined>(undefined);
  folderPath$ = new BehaviorSubject<string>('');
  subfolders: { name: string; count: number }[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private readonly customFieldCollectionService: CustomFieldCollectionService,
    private readonly nbDialogService: NbDialogService,
    private readonly nbToastrService: NbToastrService,
    private readonly currentUserService: CurrentUserService,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.searchText$,
      this.reload$,
      this.folderPath$,
    ]).pipe(
      debounceTime(300),
      takeUntil(this.destroy$),
    ).subscribe(async ([searchText, , folderPath]) => {
      this.collections$.next(undefined);
      this.splitView?.unselect();
      const category = this.normalizePath(folderPath);
      const [collections, folderNames, allItems] = await Promise.all([
        firstValueFrom(this.customFieldCollectionService.findAll({
          text: searchText.trim(),
          tenantId: this.currentTenantId(),
          sort: { sort: 'title', order: 'ASC' },
          category,
        })),
        firstValueFrom(this.customFieldCollectionService.getFolders(category || undefined)),
        firstValueFrom(this.customFieldCollectionService.findAll({ tenantId: this.currentTenantId() })),
      ]);
      this.collections$.next(collections);

      this.subfolders = folderNames.map(name => {
        const prefix = this.normalizePath(category ? `${category.replace(/\/$/, '')}/${name}` : name);
        const count = allItems.filter(item => item.category.startsWith(prefix)).length;
        return { name, count };
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return false;
  }

  get pathSegments(): string[] {
    const p = this.folderPath$.value;
    return p ? p.split('/').filter(s => s.length > 0) : [];
  }

  normalizePath(path: string): string {
    const trimmed = path.trim().replace(/^\/+|\/+$/g, '');
    return trimmed ? `${trimmed}/` : '';
  }

  navigateToRoot(): void {
    this.folderPath$.next('');
  }

  navigateToSegment(index: number): void {
    this.folderPath$.next(this.pathSegments.slice(0, index + 1).join('/'));
  }

  navigateToFolder(folderName: string): void {
    const current = this.folderPath$.value;
    this.folderPath$.next(current ? `${current}/${folderName}` : folderName);
  }

  openFolderDialog(): void {
    if (this.folderDialog) {
      this.nbDialogService.open(this.folderDialog);
    }
  }

  async showAddDialog(): Promise<void> {
    const result = await firstValueFrom(this.nbDialogService.open(CustomFieldCollectionDialogComponent, {
      context: { folderPath: this.normalizePath(this.folderPath$.value) },
    }).onClose);
    if (result) {
      await this.save(result);
    }
  }

  async save(formValue: CustomFieldMergeDto): Promise<void> {
    await firstValueFrom(this.customFieldCollectionService.merge(formValue));
    this.nbToastrService.success('Benutzerdefinierte Gruppe gespeichert', 'Erfolg');
    this.reload$.next(undefined);
  }

  async deleteDialog(): Promise<boolean> {
    return await firstValueFrom(this.nbDialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Gruppe löschen?',
        message: 'Möchten Sie die benutzerdefinierte Gruppe wirklich löschen?',
      },
    }).onClose);
  }

  async delete(id: string): Promise<void> {
    await firstValueFrom(this.customFieldCollectionService.deleteById(id));
    this.nbToastrService.success('Benutzerdefinierte Gruppe gelöscht', 'Erfolg');
    this.reload$.next(undefined);
    this.splitView?.unselect();
  }
}
